import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Input } from 'components/Form/Input'
import { Option, Select } from 'components/Select/Select'
import { Multiselect } from 'components/Select/Multiselect'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { Heading2, NormalText } from 'styling/TypographyV2'
import { CloseIcon } from 'assets/icons/CloseIcon'
import {
  createOperationalTeam,
  getSimpleOperationalTeams,
  getSupervisorCandidates,
} from 'features/operationalManagement/operationalManagementSlice'
import {
  TEAM_TYPE_LABEL,
  TeamType,
} from 'features/operationalManagement/operationalManagement.types'
import { ModalFieldHelp, ModalLabel, ModalRow } from '../Organogram.styled'

interface CreateTeamModalProps {
  onClose: () => void
  onCreated: () => void
}

const typeOptions = Object.entries(TEAM_TYPE_LABEL).map(([value, label]) => ({
  label,
  value: Number(value),
}))

export const CreateTeamModal = ({ onClose, onCreated }: CreateTeamModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { simpleTeams, supervisorCandidates, creatingTeam } = useSelector(
    (state: RootState) => state.operationalManagement,
  )

  const [name, setName] = useState('')
  const [type, setType] = useState<TeamType | null>(null)
  const [supervisorIds, setSupervisorIds] = useState<number[]>([])
  const [parentTeamId, setParentTeamId] = useState<number | null>(null)
  const [supervisorsFetched, setSupervisorsFetched] = useState(false)

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
    if (!supervisorsFetched) {
      setSupervisorsFetched(true)
      dispatch(getSupervisorCandidates(undefined))
    }
  }, [supervisorsFetched])

  const parentTeamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )
  const supervisorOptions = useMemo<Option[]>(
    () => supervisorCandidates.map((u) => ({ label: u.fullName, value: u.id })),
    [supervisorCandidates],
  )
  const supervisorValues = useMemo<Option[]>(
    () => supervisorOptions.filter((o) => supervisorIds.includes(o.value as number)),
    [supervisorOptions, supervisorIds],
  )
  const typeOptionsTyped = useMemo<Option[]>(
    () => typeOptions.map((o) => ({ label: o.label, value: o.value })),
    [],
  )
  const selectedType = type == null ? null : typeOptionsTyped.find((o) => o.value === type) ?? null
  const selectedParent =
    parentTeamId == null ? null : parentTeamOptions.find((o) => o.value === parentTeamId) ?? null
  const nullParent: Option = { label: 'No parent team', value: null }

  const trimmedName = name.trim()
  const canSubmit = Boolean(trimmedName && type) && !creatingTeam

  const handleSave = async () => {
    if (!canSubmit || type == null) return
    const result = await dispatch(
      createOperationalTeam({
        name: trimmedName,
        type,
        ...(supervisorIds.length > 0 ? { supervisorIds } : {}),
        ...(parentTeamId != null ? { parentTeamId } : {}),
      }),
    )
    if (createOperationalTeam.fulfilled.match(result)) {
      onCreated()
      onClose()
    }
  }

  return (
    <Modal onClose={onClose}>
      <ModalContent maxWidth='520px'>
        <ModalHeader>
          <Flex between center>
            <Heading2>Create Team</Heading2>
            <CloseIcon onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <NormalText mb='1.25rem'>
            Teams are the primary operational grouping (OrgGroupID)
          </NormalText>

          <ModalRow>
            <Input
              label='Team name *'
              placeholder='E.g. Charlie Crew'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </ModalRow>

          <ModalRow>
            <ModalLabel>Team type *</ModalLabel>
            <Select
              placeholder='Select a type'
              options={typeOptionsTyped}
              value={selectedType}
              onChange={(opt) => setType(opt.value == null ? null : (opt.value as TeamType))}
            />
          </ModalRow>

          <ModalRow>
            <ModalLabel>Assign Supervisors</ModalLabel>
            <Multiselect
              options={supervisorOptions}
              values={supervisorValues}
              onChange={(vals) =>
                setSupervisorIds((vals as Option[])?.map((v) => v.value as number))
              }
              placeholder='Select supervisors'
            />
            <ModalFieldHelp>
              If left empty, team will appear as &quot;Missing Supervisor&quot; in alerts
            </ModalFieldHelp>
          </ModalRow>

          <ModalRow>
            <ModalLabel>Parent team (optional)</ModalLabel>
            <Select
              placeholder='No parent team'
              options={parentTeamOptions}
              nullValue={nullParent}
              value={selectedParent}
              onChange={(opt) => setParentTeamId(opt.value == null ? null : (opt.value as number))}
            />
            <ModalFieldHelp>
              Used for sub-team grouping; does not affect assignment scope
            </ModalFieldHelp>
          </ModalRow>
        </ModalBody>
        <ModalFooter>
          <Button variant='secondary' onClick={onClose} disabled={creatingTeam}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSave} disabled={!canSubmit}>
            {creatingTeam ? 'Saving…' : '+ Create team'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
